<template>
  <div class="flex flex-col h-full">
    <div class="w-full flex-1 min-h-0 overflow-y-scroll">
      <div class="flex flex-row items-center pt-4 px-4 justify-between gap-4">
        <div class="flex content-center items-center">
          <div @click="utils.goBack(router, backLink)" class="pr-2 flex items-center cursor-pointer">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-6 h-6"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </div>
          <h3 class="text-2xl font-bold">Recolección #{{ recoleccionId }}</h3>
        </div>

        <div v-if="recoleccion">
          <div :class="headerStatusStyle(recoleccion.estado)" class="rounded-full text-center w-auto py-1 px-4">
            {{ collectionStatus(recoleccion.estado).label }}
          </div>
        </div>
      </div>

      <template v-if="recoleccion">
      <div class="rounded border bg-white m-4">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4">
          <div v-if="isAdmin">
            <label class="block text-gray-400 text-sm font mb-0">Proveedor</label>
            <h3 class="text-xl font-semibold">{{ recoleccion.proveedor.nombre }}</h3>
          </div>
          <div>
            <label class="block text-gray-400 text-sm font mb-0">Fecha</label>
            <h3 class="text-xl font-semibold">{{ formatDate(recoleccion.solicitada_at) }}</h3>
          </div>
          <div class="sm:col-span-2">
            <label class="block text-gray-400 text-sm font mb-0">Dirección de recolección</label>
            <div v-if="recoleccion.direccion">
              <p>
                {{ recoleccion.direccion.direccion_calle }}
                {{ recoleccion.direccion.direccion_exterior }}
              </p>
              <p>
                {{ recoleccion.direccion.direccion_colonia }}
                {{ recoleccion.direccion.direccion_codigo }}
              </p>
              <p>{{ recoleccion.direccion.direccion_estado }}</p>
            </div>
            <p v-else>Sin dirección</p>
          </div>
        </div>

        <div v-if="isAdmin && recoleccion.estado === 'solicitada'" class="flex flex-row px-4 pb-4 gap-4">
          <button
            class="w-full bg-primary hover:bg-primary-dark rounded text-white font-bold py-2 px-4"
            @click="resolve('recibida')"
          >
            Marcar recibida
          </button>
        </div>
      </div>

      <h3 class="text-xl font-bold px-4">
        Órdenes ({{ orders.length }})
      </h3>
      <inbox
        v-if="orders.length > 0"
        class="mt-4 pb-4"
        :user="isAdmin ? 'admin' : 'proveedor'"
        :ordenes="orders"
        :fields="{estado: true, cliente: true, surtir: false, pago: false, aprobar: false, facturar: false, entregar: true}"
        :collection-received="isAdmin && recoleccion.estado === 'recibida'"
        :disable-actions="true"
        :hide-actions="true"
        @order-cancelled="load"
      ></inbox>
      <empty v-else :texto="'Esta recolección no tiene órdenes.'" />
      </template>

      <empty v-if="hasLoaded && !loading && !recoleccion" class="mt-4" :texto="'No se encontró la recolección.'" />
    </div>

    <footer
      v-if="recoleccion && recoleccion.estado === 'solicitada'"
      class="flex-none border-t border-gray-200 bg-white p-4"
    >
      <button
        class="w-full bg-red hover:bg-red-dark rounded text-white font-bold py-2 px-4"
        @click="resolve('cancelada')"
      >
        Cancelar recolección
      </button>
    </footer>

  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toast-notification";
import { store } from "../store.js";
import { utils } from "./utils/utils.js";
import { collectionStatus } from "./utils/recolecciones.js";

export default {
  name: "RecoleccionDetalle",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const recoleccion = ref(null);
    const loading = ref(false);
    const hasLoaded = ref(false);
    const isAdmin = computed(() => route.path.startsWith("/admin/"));
    const recoleccionId = computed(() => route.params.recoleccion_id);
    const proveedorId = computed(() => route.params.proveedor_id || null);
    const backLink = computed(() =>
      isAdmin.value
        ? "/admin/recolecciones"
        : `/proveedor/${proveedorId.value}/recolecciones`
    );
    const orders = computed(() =>
      (recoleccion.value?.recoleccion_pedidos || [])
        .map((relation) => {
          if (!relation.pedido) return null;
          return {
            ...relation.pedido,
            recoleccion_pedidos: [
              ...(relation.pedido.recoleccion_pedidos || []),
              {
                recoleccion: {
                  id: recoleccion.value.id,
                  estado: recoleccion.value.estado,
                  solicitada_at: recoleccion.value.solicitada_at,
                  recibida_at: recoleccion.value.recibida_at,
                  cancelada_at: recoleccion.value.cancelada_at,
                },
              },
            ],
          };
        })
        .filter(Boolean)
    );
    async function load() {
      loading.value = true;
      hasLoaded.value = false;
      try {
        const collections = await utils.getRecolecciones(
          null,
          proveedorId.value,
          route.params.recoleccion_id
        );
        recoleccion.value = collections[0] || null;
        hasLoaded.value = true;
      } catch (error) {
        if (error.status !== 401) {
          useToast().error("No se pudo cargar la recolección", { position: "top-right" });
        }
      } finally {
        loading.value = false;
      }
    }

    async function resolve(nextStatus) {
      if (!confirm(`¿Quieres marcar esta recolección como ${nextStatus}?`)) return;
      try {
        await utils.actualizarEstadoRecoleccion(
          recoleccion.value.id,
          nextStatus,
          isAdmin.value ? null : proveedorId.value
        );
        await load();
        useToast().success(`Recolección ${nextStatus}`, { position: "top-right" });
      } catch (error) {
        useToast().error(error.message || "No se pudo actualizar la recolección", { position: "top-right" });
      }
    }

    function formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleDateString("en-GB", {
        timeZone: "America/Mexico_City",
      });
    }

    function headerStatusStyle(estado) {
      if (estado === "solicitada") return "bg-yellow-500 text-white";
      if (estado === "recibida") return "bg-primary text-white";
      return "border border-gray-300 text-gray-300";
    }

    onMounted(() => {
      if (!isAdmin.value || store.authAdmin) load();
    });
    watch(
      () => (isAdmin.value ? store.authAdmin : store.auth),
      (authorized) => {
        if (authorized) load();
      }
    );

    return {
      recoleccion,
      loading,
      hasLoaded,
      isAdmin,
      recoleccionId,
      router,
      backLink,
      orders,
      load,
      resolve,
      utils,
      collectionStatus,
      headerStatusStyle,
      formatDate,
    };
  },
};
</script>
